import React, { useRef, useCallback, useEffect } from 'react'

import AtomicSDK, {
  AACHeadlessStreamContainer,
  AACStreamContainer,
} from '@atomic.io/action-cards-web-sdk'
import { useAtomic } from './AtomicProvider'
import { Box, Modal, Stack, styled } from '@mui/material'
import { log } from '../../util/log'

const AtomicStyleWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
`

interface AtomicEmbedProps {
  containerId: string
}

const AtomicTakeover: React.FC<AtomicEmbedProps> = ({ containerId }) => {
  const { atomicReady, onLinkButtonPressed } = useAtomic()
  const [{ totalCards }, setCardCount] = React.useState<{
    cardCount: number
    totalCards: number
  }>({ cardCount: 0, totalCards: 0 })

  // Create a reference to the embedded stream container
  const embedRef = useRef<AACStreamContainer>()
  const observableStream = React.useRef<AACHeadlessStreamContainer>()

  const updateCardCount = useCallback(() => {
    if (atomicReady) {
      AtomicSDK.requestUserMetrics()
        .then((metrics) => {
          const cardCount = metrics.unseenCardsForStreamContainer(containerId)
          const totalCards = metrics.totalCardsForStreamContainer(containerId)
          setTimeout(() => {
            setCardCount({ cardCount, totalCards })
          }, 1000)
          log({ cardCount, totalCards }, containerId)
        })
        .catch((err) => {
          console.error(err)
        })
    }
  }, [atomicReady, containerId])

  // Adding an additional check
  useEffect(() => {
    AtomicSDK.observeSDKEvents((event) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (AtomicSDK.userMetricsTriggerEvents.has(event.eventName as unknown as any)) {
        updateCardCount()
      }
    })
  }, [updateCardCount])

  useEffect(() => {
    if (atomicReady) {
      // Initialise and Observe takeover stream changes in headless mode
      observableStream.current = AtomicSDK.observableStreamContainer({
        streamContainerId: containerId,
        onCardCountChanged() {
          updateCardCount()
        },
        cardFeedHandler() {
          return null
        },
      })

      observableStream.current?.start()
      return () => {
        observableStream.current?.stop()
      }
    }
  }, [updateCardCount])

  const singleCardEmbed = useCallback(
    (el: HTMLDivElement) => {
      if (!atomicReady) {
        return
      }
      if (embedRef.current) {
        // Clean up any existing instance we might have
        embedRef.current.stop()
      }
      if (el && atomicReady) {
        el.innerHTML = ''

        AtomicSDK.singleCard(el, {
          streamContainerId: containerId,
          enabledUiElements: {
            launcherButton: {
              disabled: true,
            },
          },
          onSubmitButtonPressed(payload) {
            onLinkButtonPressed(payload)
          },
          onLinkButtonPressed(payload) {
            onLinkButtonPressed(payload)
          },
        })
      }
    },
    [updateCardCount],
  )

  if (!atomicReady) {
    return null
  }

  return (
    <Modal
      open={totalCards !== 0}
      onClose={(_, reason) => {
        if (reason === 'backdropClick') {
          // Adding a final backstop incase we brick the background
          updateCardCount()
        }
      }}
    >
      <Stack
        width='100%'
        maxWidth='500px'
        height='100%'
        margin='auto'
        display='grid'
        sx={{
          outline: 'none',
        }}
      >
        <Box width='100%' maxWidth='500px' margin='auto' p={2}>
          <AtomicStyleWrapper ref={singleCardEmbed} className='atomic-embed-single-card' />
        </Box>
      </Stack>
    </Modal>
  )
}

export default AtomicTakeover
